import React, {useCallback} from "react";
import { useHistory } from "react-router";
import UnlockIcon from "@atlaskit/icon/glyph/unlock";
import LockIcon from "@atlaskit/icon/glyph/lock";

import { useModalContext } from "../../contexts/ModalContext/context";
import { useSnackbar } from "../../hooks/useSnackbar";
import useAuth from "../../routes/login/AuthContext";
import {
  LockUserInRoom,
  LockAllUsersInRoom, 
  UnlockUserInRoom,
  UnlockAllUsersInRoom,
} from "../../services/RoommateApi";
import { Modal } from "../Modal";
import { Button } from "../../UI/Button";
import { Box } from "../../UI/Box";
import { Typography } from "../../UI/Typography";
import { EMAIL_ADDRESS, PHONE_NUMBER } from "../../constants/general";
import { URL } from "../../constants/navigation";
import { Link } from "../Link";

interface IProps {
  adminLock: boolean;
  l1Id?: number | null;
  l2Id?: number | null;
  l3Id?: number | null;
  membershipIds?: string[];
  lockUnlockAllUsers?: boolean;
  membershipIdsGroups?: string[][];
  onSuccess: VoidFunction;
}

export const UserRoommateLockModal: React.FC<IProps> = (props) => {
  const { push } = useHistory();
  const { setIsOpen } = useModalContext();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = React.useState(false);
  const { getAuthToken } = useAuth();

  const onHide = () => {
    setIsOpen(false);
  };
  const navToMatchRules = useCallback((e) => {
    e.preventDefault();
    push(URL.MATCH_RULES);
  }, []);

  const unlockUserInRoom = async (membershipIds: string[]) => {
    if (!props?.l1Id || !props?.l2Id || !props?.l3Id) return;

    return UnlockUserInRoom(props.l1Id, props.l2Id, props.l3Id, membershipIds, await getAuthToken());
  };

  const unlockAllUsersInRoom = async () => {
    if (!props?.l1Id || !props?.l2Id || !props?.l3Id) return;

    return UnlockAllUsersInRoom(props.l1Id, props.l2Id, props.l3Id, await getAuthToken());
  };

  const lockUserInRoom = async (membershipIds: string[]) => {
    if (!props?.l1Id || !props?.l2Id || !props?.l3Id) return;

    return LockUserInRoom(props.l1Id, props.l2Id, props.l3Id, membershipIds, await getAuthToken());
  };

  const lockAllUsersInRoom = async () => {
    if (!props?.l1Id || !props?.l2Id || !props?.l3Id) return;

    return LockAllUsersInRoom(props.l1Id, props.l2Id, props.l3Id, await getAuthToken());
  };

  const handleMembershipIds = async (membershipIds: string[]) => {
    if (props.adminLock) {
      const success = await unlockUserInRoom(membershipIds);

      if (success) {
        if (props.onSuccess) {
          props.onSuccess();
        }
        showSuccessSnackbar("Successfully completed");
      } else {
        showErrorSnackbar("An error occured. Please try again");
      }
    } else {
      const success = await lockUserInRoom(membershipIds);

      if (success) {
        if (props.onSuccess) {
          props.onSuccess();
        }
        showSuccessSnackbar("Successfully completed");
      } else {
        showErrorSnackbar("An error occured. Please try again");
      }
    }
  };

  const handleLockUnlockAllUsers = async () => {
    if (props.adminLock) {
      const success = await unlockAllUsersInRoom();

      if (success) {
        if (props.onSuccess) {
          props.onSuccess();
        }
        showSuccessSnackbar("All Matches Have Been Unlocked");
      } else {
        showErrorSnackbar("An error occured. Please try again");
      }
    } else {
      const success = await lockAllUsersInRoom();

      if (success) {
        if (props.onSuccess) {
          props.onSuccess();
        }
        showSuccessSnackbar("All Matches Have Been Locked In");
      } else {
        showErrorSnackbar("An error occured. Please try again");
      }
    }
  };


  const handleActionClick = async () => {
    setIsLoading(true);
    if (!props?.l1Id || !props?.l2Id || !props?.l3Id) return;

    try {
      if (props.membershipIds?.length && !props.lockUnlockAllUsers) {
        await handleMembershipIds(props.membershipIds);
      }
      if (props.lockUnlockAllUsers) {
        await handleLockUnlockAllUsers();
      }

      if (props.membershipIdsGroups?.length) {
        await Promise.all(props.membershipIdsGroups.map(handleMembershipIds));
      }
    } catch (error) {
      showErrorSnackbar("An error occured. Please try again");
    }
    setIsLoading(false);
    onHide();
  };
  const groupTitle = props.adminLock? "Unlock Roommate Group" : "Lock In Roommate Group";
  const allTitle = props.adminLock? "Unlock All Roommate Matches?" : "Lock In All Roommate Matches?";
  const title = props.lockUnlockAllUsers ? allTitle : groupTitle;

  const actionLabel = props.adminLock ? "Unlock" : "Lock In";

  const groupBodyText = props.adminLock
  ? "By clicking 'Unlock', users will be able to leave this roommate group."
  : "By clicking 'Lock In', users in this roommate group will be unable to leave the group. More users can join the group if it is not full.";

  const allBodyText = (adminLock: boolean) => {
    if (adminLock) {
      return (<>
        By clicking 'Unlock', all users in this Unit Type will be unlocked from their current roommates and will be able to leave their matches as long as the <Link target="_blank" onClick={navToMatchRules} href={URL.MATCH_RULES}>matching dates</Link> are active.
      </>);
    } else {
      return "By clicking 'Lock In', all users in this Unit Type will be locked with their current roommates and will not be able to leave their matches. More users can still join the group if it is not full.";
    }
  };
  
  const bodyText = props.lockUnlockAllUsers ? allBodyText(props.adminLock) : groupBodyText;

  const actionButtonIcon = props.adminLock ? <UnlockIcon label="Unlock" /> : <LockIcon label="Lock" />;

  return (
    <Modal
      title={title}
      actions={
        <>
          <Button appearance="subtle" onClick={onHide} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button appearance="primary" onClick={handleActionClick} isLoading={isLoading} iconBefore={actionButtonIcon}>
            {actionLabel}
          </Button>
        </>
      }
    >
      <Box px={3} py={2}>
        <Typography variant="body2" gutterBottom>
          {bodyText}
        </Typography>
        <Typography variant="body2" gutterBottom>
          If you have questions about how this tool works, please call us at
          {PHONE_NUMBER} or e-mail <Link href={`mailto:${EMAIL_ADDRESS}`}>{EMAIL_ADDRESS}</Link>
        </Typography>
      </Box>
    </Modal>
  );
};
